/* import __COLOCATED_TEMPLATE__ from './unified-filter.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

interface CategoryOption {
  id: string;
  title: string;
  description: string;
  icon: InterfaceIconName;
}

interface Signature {
  Args: {
    categories: CategoryOption[];
    selectedCategories: string[];
    onCategoryFilterChange: (categories: string[]) => void;
    showChannels?: boolean;
    selectedChannels: string[];
    onChannelFilterChange: (channels: string[]) => void;
    showAudiences?: boolean;
    selectedAudienceIds: number[];
    onAudienceFilterChange: (audienceIds: number[]) => void;
  };
}

const CHANNEL_GROUPS = ['chat', 'email', 'voice'] as const;

export default class UnifiedFilter extends Component<Signature> {
  @service intl!: IntlService;
  @service declare aiContentSegmentsService: AiContentSegmentsService;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    if (args.showAudiences && !this.aiContentSegmentsService.isLoading) {
      this.aiContentSegmentsService.loadSegments();
    }
  }

  private get totalSelectedCount(): number {
    return (
      this.args.selectedCategories.length +
      this.args.selectedChannels.length +
      this.args.selectedAudienceIds.length
    );
  }

  get displayText(): string {
    if (this.totalSelectedCount > 0) {
      return this.intl.t('ai-agent.guidance.filters.unified.filters-with-count', {
        count: this.totalSelectedCount,
      });
    }
    return this.intl.t('ai-agent.guidance.filters.unified.filters');
  }

  get items() {
    let categoryItems = this.args.categories.map((category) => ({
      text: category.title,
      icon: category.icon,
      value: `category:${category.id}`,
      isSelected: this.args.selectedCategories.includes(category.id),
    }));

    let channelItems = CHANNEL_GROUPS.map((channel) => ({
      text: this.channelLabel(channel),
      value: `channel:${channel}`,
      isSelected: this.args.selectedChannels.includes(channel),
    }));

    let groups: {
      heading: string;
      items: { text: string; value: string; isSelected: boolean }[];
    }[] = [
      {
        heading: this.intl.t('ai-agent.guidance.filters.unified.categories'),
        items: categoryItems,
      },
    ];

    if (this.args.showChannels) {
      groups.push({
        heading: this.intl.t('ai-agent.guidance.filters.unified.channels'),
        items: channelItems,
      });
    }

    if (this.args.showAudiences) {
      let segments = this.aiContentSegmentsService.segments;
      if (segments.length > 0) {
        let audienceItems = segments.map((segment) => ({
          text: segment.nameWithFallback,
          value: `audience:${segment.id}`,
          isSelected: this.args.selectedAudienceIds.includes(Number(segment.id)),
        }));

        groups.push({
          heading: this.intl.t('ai-agent.guidance.filters.unified.audiences'),
          items: audienceItems,
        });
      }
    }

    return groups;
  }

  private channelLabel(channel: string): string {
    if (channel === 'chat') {
      return this.intl.t('ai-agent.guidance.channel-groups.chat');
    } else if (channel === 'email') {
      return this.intl.t('ai-agent.guidance.channel-groups.email');
    }
    return this.intl.t('ai-agent.guidance.channel-groups.voice');
  }

  @action
  handleSelectionChange(selectedValues: string[]) {
    let categories: string[] = [];
    let channels: string[] = [];
    let audienceIds: number[] = [];

    for (let value of selectedValues) {
      if (value.startsWith('category:')) {
        categories.push(value.replace('category:', ''));
      } else if (value.startsWith('channel:')) {
        channels.push(value.replace('channel:', ''));
      } else if (value.startsWith('audience:')) {
        audienceIds.push(Number(value.replace('audience:', '')));
      }
    }

    this.args.onCategoryFilterChange(categories);
    this.args.onChannelFilterChange(channels);
    this.args.onAudienceFilterChange(audienceIds);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::UnifiedFilter': typeof UnifiedFilter;
    'ai-agent/guidance/unified-filter': typeof UnifiedFilter;
  }
}
